import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import set from 'lodash/set';
import assign from 'lodash/assign';
import get from 'lodash/get';
import store from 'store2';
import omit from 'lodash/omit';
import includes from 'lodash/includes';
import { toast } from 'react-toastify';
import { push } from 'connected-react-router';
import { chrApi, pwsApi, chrFileUploadApi } from '../../api/api';
import { apiErrorHandler } from '../Utils/apiErrorHandler';
import { ESIGN_ENCOUNTER_NOTE, FETCH_ENCOUNTER_NOTE, FETCH_ENCOUNTER_NOTE_ACTION, UPDATE_ENCOUNTER_FORM_BY_ID } from '../Patient/constants';
import {
  batchUpdateEncounterSectionsAction,
  fetchEncounterChartImportBySectionId,
  fetchEncounterChartImportBySectionIdSuccess,
  fetchEncounterDataSuccess,
  fetchEncounterFormByIdAction,
  fetchEncounterNoteSectionsSuccess,
  fetchEncounterNoteSuccess,
  postEncounterChartImport,
  uploadEncounterNoteAttachmentAction,
  uploadEncounterNoteAttachmentSuccess,
} from './slice';
import { selectEncounterDataObject, selectEncounterNoteObject } from './selectors';
import { showLoader } from '../globalReducer';
import { populateMedicationFromChartImport } from '../RxMedications/slice';
import { populateOrderFromChartImport } from '../RxOrders/slice';

// Fetch Encounter Note

function getEncounterNoteApi(appointmentId) {
  return pwsApi('get', `encounter_note/appointment/${appointmentId}`, null, null);
}
function* getEncounterNote(action) {
  try {
    const apiResponse = yield call(getEncounterNoteApi, action.payload.appointmentId);
    yield put({
      type: fetchEncounterNoteSuccess.type,
      payload: { data: apiResponse.data },
    });
  } catch (e) {
    yield call(apiErrorHandler, e);
  }
}

export function* fetchEncounterNoteSaga() {
  yield takeLatest(FETCH_ENCOUNTER_NOTE, getEncounterNote);
}

// Fetch Encounter Note Sections

function getEncounterNoteSectionApi(appointmentId) {
  return chrApi('get', `encounter_note_section/appointment/${appointmentId}`, null, null);
}

function* getEncounterNoteSection(action) {
  try {
    const apiResponse = yield call(getEncounterNoteSectionApi, action.payload.appointmentId);
    yield put({
      type: fetchEncounterNoteSectionsSuccess.type,
      payload: { data: apiResponse.data },
    });
  } catch (e) {
    yield call(apiErrorHandler, e);
  }
}

export function* fetchEncounterNoteSectionSaga() {
  yield takeLatest(FETCH_ENCOUNTER_NOTE_ACTION, getEncounterNoteSection);
}

function markEncounterCompletedApi(id) {
  pwsApi('put', `encounter_note/recording/${id}`, { markCompleted: true }, null);
}
function batchUpdateProviderEncounterApi(obj) {
  return chrApi('put', 'encounter_batch_update', obj, null);
}
function* batchUpdateProviderEncounter(action) {
  try {
    const { markCompleted, encounterNoteId, isPatient, redirect, from, to, type, clearLocalStorage } = action.payload;
    yield put(showLoader(true));
    const encounterData = yield select(selectEncounterDataObject);
    // const providerSectionIds = get(encounterData, 'template.sections', [])
    //   .filter(sec => sec.isProviderSection)
    //   .map(sec => sec.id);
    // const encounterNoteSections = yield select(selectEncounterNoteSections);
    // const questions = yield select(selectQuestions);
    // const options = yield select(selectOptions);
    // const sectionsArr = denormalize(providerSectionIds, [encounterNoteSectionSchema], {
    //   encounterNoteSections,
    //   questions,
    //   options,
    // });
    yield call(batchUpdateProviderEncounterApi, {
      encounterData,
      encounterNoteSections: [],
    });

    if (redirect) {
      if (markCompleted && encounterNoteId) {
        yield call(markEncounterCompletedApi, encounterNoteId);
        toast.success('Encounter Date Successfully Marked Completed');
      } else {
        toast.success('Encounter Note Successfully Saved');
      }
    }
    yield put(showLoader(false));
    if (clearLocalStorage) {
      store.remove(`activeEncounter-${encounterData.id}`);
      store.remove(`physicianNotes-${encounterData.id}`);
    }
    if (redirect) {
      if (isPatient === 'true') {
        yield put(push(`/patient/${action.payload.patientId}`));
      } else {
        yield put(push(`/appointment?&from=${from}&to=${to}&type=${type}`));
      }
    }
  } catch (e) {
    yield put(showLoader(false));
    yield call(apiErrorHandler, e);
  }
}

export function* batchUpdateProviderEncounterSaga() {
  yield takeLatest(batchUpdateEncounterSectionsAction.type, batchUpdateProviderEncounter);
}

function updateEncounterDataApi(obj) {
  return chrApi('put', `encounter/${obj.id}`, obj, null, 2);
}

function updateEncounterNoteApi(data) {
  return pwsApi('put', `encounter_note/${data.id}`, data, null);
}

function* eSignEncounterNote(action) {
  try {
    const { isPatient } = action.payload.data;
    yield put(showLoader(true));
    let encounterNote = yield select(selectEncounterNoteObject);
    encounterNote = Object.assign({}, encounterNote, { isApproved: true });
    const encounterData = yield select(selectEncounterDataObject);
    yield call(updateEncounterDataApi, encounterData);
    yield call(updateEncounterNoteApi, encounterNote);
    yield put(showLoader(false));
    toast.success('Encounter Note Successfully E-Signed');
    if (isPatient === 'true') {
      yield put(push(`/patient/${encounterData.patientId}`));
    } else {
      yield put(push('/appointment'));
    }
  } catch (e) {
    yield put(showLoader(false));
    yield call(apiErrorHandler, e);
  }
}

export function* eSignEncounterNoteSaga() {
  yield takeLatest(ESIGN_ENCOUNTER_NOTE, eSignEncounterNote);
}

function* updateEncounterById(action) {
  try {
    const { isPatient, from, to, type, clearLocalStorage = true, pathname, activeApptId, appointmentId, activeProviderId } = action.payload.data;
    let fullPath = pathname;
    if (activeProviderId) {
      fullPath += `?providerId=${activeProviderId}`;
    }
    yield put(showLoader(true));
    const encounterData = yield select(selectEncounterDataObject);
    yield call(updateEncounterDataApi, encounterData);
    toast.success('Encounter Date Successfully Saved');
    yield put(showLoader(false));
    if (isPatient === 'true') {
      yield put(push(appointmentId || activeApptId ? `/patient/${encounterData.patientId}?activeApptId=${activeApptId || appointmentId}` : `/patient/${encounterData.patientId}`));
    } else {
      yield put(push(pathname === '/appointment' ? `/appointment?from=${from}&to=${to}&type=${type}` : fullPath));
    }
    if (clearLocalStorage) {
      store.remove(`activeEncounter-${encounterData.id}`);
      store.remove(`physicianNotes-${encounterData.id}`);
    }
  } catch (e) {
    yield put(showLoader(false));
    yield call(apiErrorHandler, e);
  }
}

export function* updateEncounterDataSaga() {
  yield takeLatest(UPDATE_ENCOUNTER_FORM_BY_ID, updateEncounterById);
}

function uploadEncounterNoteAttachmentApi({ data, appointmentId }) {
  return chrFileUploadApi(`encounter_physician_images/${appointmentId}`, data);
}
function* uploadEncounterNoteAttachment(action) {
  try {
    yield put(showLoader(true));
    const apiResponse = yield call(uploadEncounterNoteAttachmentApi, action.payload);
    yield put({
      type: uploadEncounterNoteAttachmentSuccess.type,
      payload: { data: apiResponse.data },
    });
    yield put(showLoader(false));
  } catch (e) {
    yield put(showLoader(false));
    yield call(apiErrorHandler, e);
  }
}

export function* uploadEncounterNoteAttachmentSaga() {
  yield takeLatest(uploadEncounterNoteAttachmentAction.type, uploadEncounterNoteAttachment);
}

function fetchEncounterByIdApi(id) {
  return chrApi('get', `encounter/${id}`, null);
}
function* fetchEncounterById(action) {
  try {
    yield put(showLoader(true));
    const apiResponse = yield call(fetchEncounterByIdApi, action.payload.encounterId);
    yield put({
      type: fetchEncounterDataSuccess.type,
      payload: { data: apiResponse.data },
    });
    yield put(showLoader(false));
  } catch (e) {
    yield put(showLoader(false));
    yield call(apiErrorHandler, e);
  }
}

export function* fetchEncounterByIdSaga() {
  yield takeLatest(fetchEncounterFormByIdAction.type, fetchEncounterById);
}

function fetchEncounterChartImportBySectionIdApi({ encounterDataId, sectionId }) {
  return chrApi('get', `chart_import/encounter/${encounterDataId}/section/${sectionId} `, null);
}
function* getEncounterChartImportBySectionIdApi(action) {
  try {
    const { sectionId, title } = action.payload;
    yield put(showLoader(true));
    const { data } = yield call(fetchEncounterChartImportBySectionIdApi, action.payload);
    const { currentData = [] } = data;
    yield put(showLoader(false));
    if (title === 'Prescriptions') {
      yield put(populateMedicationFromChartImport({ data: currentData }));
    }
    if (title === 'Orders') {
      yield put(populateOrderFromChartImport({ data: currentData }));
    }
    yield put(fetchEncounterChartImportBySectionIdSuccess({ data, sectionId }));
  } catch (e) {
    yield put(showLoader(false));
    yield call(apiErrorHandler, e);
  }
}

export function* fetchEncounterChartImportBySectionIdApiSaga() {
  yield takeEvery(fetchEncounterChartImportBySectionId.type, getEncounterChartImportBySectionIdApi);
}

function postEncounterChartImportApi(data) {
  return chrApi('post', 'chart_import', omit(data, ['sectionTitle']), null);
}
function* createEncounterChartImportApi(action) {
  try {
    const { sectionId, sectionTitle } = action.payload;
    yield put(showLoader(true));
    let { data } = yield call(postEncounterChartImportApi, action.payload);
    if (!data) {
      if (includes(['Prescriptions', 'Orders', 'Diagnoses'], sectionTitle)) {
        data = [];
      } else {
        data = {};
      }
    }
    if (sectionTitle === 'Prescriptions') {
      yield put(populateMedicationFromChartImport({ data }));
    }
    if (sectionTitle === 'Orders') {
      yield put(populateOrderFromChartImport({ data }));
    }
    yield put(fetchEncounterChartImportBySectionIdSuccess({ data: { currentData: data, pastData: [] }, sectionId, skipPastData: true }));

    yield put(showLoader(false));
  } catch (e) {
    yield put(showLoader(false));
    yield call(apiErrorHandler, e);
  }
}

export function* postEncounterChartImportApiSaga() {
  yield takeLatest(postEncounterChartImport.type, createEncounterChartImportApi);
}
